import { create } from 'zustand';
import { api } from '@/lib/api';
import type { Prompt } from '@/types/prompt';

interface PromptStore {
  prompts: Prompt[];
  isLoading: boolean;
  error: string | null;
  
  // 分页信息
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
  
  // 筛选条件
  filters: {
    category?: string;
    permission?: string;
    search?: string;
  };
  
  // Actions
  loadPrompts: (params?: {
    category?: string;
    permission?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => Promise<void>;
  
  getPromptById: (id: string) => Promise<Prompt | null>;

  createPrompt: (data: Omit<Prompt, 'createdAt' | 'updatedAt' | 'createdBy'>) => Promise<Prompt>;

  updatePrompt: (id: string, data: Omit<Partial<Prompt>, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>) => Promise<Prompt>;
  
  deletePrompt: (id: string) => Promise<void>;
  
  setFilters: (filters: {
    category?: string;
    permission?: string;
    search?: string;
  }) => void;
  
  clearFilters: () => void;
  
  reset: () => void;
}

export const usePromptStore = create<PromptStore>((set, get) => ({
  prompts: [],
  isLoading: false,
  error: null,
  pagination: null,
  filters: {},
  
  loadPrompts: async (params) => {
    try {
      set({ isLoading: true, error: null });
      
      const response = await api.prompts.list(params);
      
      if (response.success && response.data) {
        set({
          prompts: response.data.prompts || [],
          pagination: response.data.pagination || null,
          isLoading: false,
        });
      } else {
        throw new Error(response.error?.message || '加载 Prompt 失败');
      }
    } catch (error) {
      console.error('Failed to load prompts:', error);
      set({
        error: error instanceof Error ? error.message : '加载 Prompt 失败',
        isLoading: false,
      });
    }
  },
  
  getPromptById: async (id: string) => {
    try {
      // 先从本地缓存查找
      const cached = get().prompts.find(p => p.id === id);
      if (cached) {
        return cached;
      }
      
      // 从 API 获取
      const response = await api.prompts.get(id);
      
      if (response.success && response.data?.prompt) {
        return response.data.prompt;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to get prompt:', error);
      return null;
    }
  },
  
  createPrompt: async (data) => {
    try {
      set({ isLoading: true, error: null });
      
      const response = await api.prompts.create(data);
      
      if (response.success && response.data?.prompt) {
        const newPrompt = response.data.prompt;
        
        // 添加到本地状态
        set(state => ({
          prompts: [newPrompt, ...state.prompts],
          isLoading: false,
        }));
        
        return newPrompt;
      } else {
        throw new Error(response.error?.message || '创建 Prompt 失败');
      }
    } catch (error) {
      console.error('Failed to create prompt:', error);
      set({
        error: error instanceof Error ? error.message : '创建 Prompt 失败',
        isLoading: false,
      });
      throw error;
    }
  },
  
  updatePrompt: async (id: string, data: Partial<Prompt>) => {
    try {
      set({ isLoading: true, error: null });
      
      const response = await api.prompts.update(id, data);
      
      if (response.success && response.data?.prompt) {
        const updatedPrompt = response.data.prompt;
        
        // 更新本地状态
        set(state => ({
          prompts: state.prompts.map(p => 
            p.id === id ? updatedPrompt : p
          ),
          isLoading: false,
        }));
        
        return updatedPrompt;
      } else {
        throw new Error(response.error?.message || '更新 Prompt 失败');
      }
    } catch (error) {
      console.error('Failed to update prompt:', error);
      set({
        error: error instanceof Error ? error.message : '更新 Prompt 失败',
        isLoading: false,
      });
      throw error;
    }
  },
  
  deletePrompt: async (id: string) => {
    try {
      set({ isLoading: true, error: null });
      
      const response = await api.prompts.delete(id);
      
      if (response.success) {
        // 从本地状态移除
        set(state => ({
          prompts: state.prompts.filter(p => p.id !== id),
          isLoading: false,
        }));
      } else {
        throw new Error(response.error?.message || '删除 Prompt 失败');
      }
    } catch (error) {
      console.error('Failed to delete prompt:', error);
      set({
        error: error instanceof Error ? error.message : '删除 Prompt 失败',
        isLoading: false,
      });
      throw error;
    }
  },
  
  setFilters: (filters) => {
    set({ filters });
  },
  
  clearFilters: () => {
    set({ filters: {} });
  },
  
  reset: () => {
    set({
      prompts: [],
      isLoading: false,
      error: null,
      pagination: null,
      filters: {},
    });
  },
}));

